import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, Clock, Users, Car, Lightbulb, Coffee, Trophy, Calendar, Heart, Share2, MessageCircle } from 'lucide-react';
import api from '../services/api';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { toast } from '../hooks/use-toast';
import { FACILITY_IMAGES, SPORT_LABELS, DEFAULT_VENUE_IMAGE } from '../config/venueConfig';
import { EventCard } from './EventsPage';
import { CoachingCard } from './CoachingPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  { icon: Coffee, label: 'Cafe' },
  { icon: Car, label: 'Parking' },
  { icon: Lightbulb, label: 'Floodlights' },
  { icon: Users, label: 'Coaching' },
];

const VenueDetail = () => {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState([]);
  const [gamesError, setGamesError] = useState('');
  const [allFacilities, setAllFacilities] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [coachingProgrammes, setCoachingProgrammes] = useState([]);
  const { user, isAuthenticated, getToken } = useCustomerAuth();

  const isFavourite = (user?.favorite_venues || []).includes(facility?.id || venueId);

  const toggleFavourite = async () => {
    if (!isAuthenticated) return;
    const targetId = facility?.id || venueId;
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/public/auth/toggle-favorite?venue_id=${targetId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast({ title: data.action === 'added' ? 'Added to favourites' : 'Removed from favourites' });
        window.location.reload();
      } else {
        throw new Error(`Server returned ${res.status}`);
      }
    } catch (err) {
      console.error('Toggle favourite failed:', err);
      toast({
        title: 'Could not update favourite',
        description: 'Please check your connection and try again.',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    loadData();
  }, [venueId]);

  // Once we have the facility, if the URL is using the internal id but a
  // slug exists, swap the address bar to /venue/{slug} without a navigation.
  // Crawlers already got their OG tags via /api/share/venues/*; this is
  // purely for humans landing on a legacy /venue/{id} link.
  useEffect(() => {
    if (!facility || !facility.slug) return;
    if (venueId === facility.slug) return;
    try {
      window.history.replaceState(null, '', `/venue/${facility.slug}`);
    } catch (e) {
      // history.replaceState is non-critical; swallow to avoid breaking render.
    }
  }, [facility, venueId]);

  // Fetch this venue's published events (max 5 displayed)
  useEffect(() => {
    const fid = facility?.id;
    if (!fid) return;
    fetch(`${API_URL}/api/events/public/list?venue_id=${encodeURIComponent(fid)}`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setUpcomingEvents(Array.isArray(d) ? d.slice(0, 5) : []))
      .catch(() => setUpcomingEvents([]));
  }, [facility]);

  // Fetch this venue's published coaching programmes (max 5)
  useEffect(() => {
    const fid = facility?.id;
    if (!fid) return;
    fetch(`${API_URL}/api/coaching/public/list?venue_id=${encodeURIComponent(fid)}&status=open&limit=5`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setCoachingProgrammes(Array.isArray(d) ? d.slice(0, 5) : []))
      .catch(() => setCoachingProgrammes([]));
  }, [facility]);

  const loadData = async () => {
    try {
      const facilities = await api.getPublicFacilities();
      setAllFacilities(facilities);
      // venueId can be either the slug (new) or the internal id (legacy).
      const found = facilities.find(f => f.slug === venueId) || facilities.find(f => f.id === venueId);
      setFacility(found);

      // Load venue games — match by both id and slug so legacy game docs still resolve.
      try {
        const res = await fetch(`${API_URL}/api/community-games/public`);
        if (res.ok) {
          const data = await res.json();
          const targetId = found?.id || venueId;
          const targetSlug = found?.slug;
          const venueGames = (Array.isArray(data) ? data : [])
            .filter(g => (g.status === 'open' || g.status === 'upcoming') && (
              g.venue_id === targetId || g.facility_id === targetId ||
              (targetSlug && (g.venue_id === targetSlug || g.facility_id === targetSlug))
            ))
            .slice(0, 5);
          setGames(venueGames);
        } else {
          throw new Error(`HTTP ${res.status}`);
        }
      } catch (gamesErr) {
        console.error('Failed to load venue community games:', gamesErr);
        setGamesError('Could not load games for this venue right now.');
      }
    } catch (err) {
      console.error('Error loading facility:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-[#0A2C93] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!facility) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
        <p className="font-body text-[#737373] mb-4">Venue not found</p>
        <button onClick={() => navigate('/')} className="font-heading font-semibold text-[#0A2C93]">Back to Home</button>
      </div>
    );
  }

  const heroImg = FACILITY_IMAGES[facility.id] || DEFAULT_VENUE_IMAGE;
  const sports = (facility.sports || []).map(s => s === 'pickle' ? 'pickleball' : s);
  const courts = facility.courts || [];
  const relatedVenues = allFacilities.filter(f => f.id !== facility.id).slice(0, 3);

  // Canonical share URL — prefer clean slug URL for humans; the backend
  // /api/share/venues/{slug|id} endpoint powers OG previews for crawlers.
  const shareSlugOrId = facility.slug || facility.id;
  const publicUrl = `${window.location.origin}/venue/${shareSlugOrId}`;
  const shareApiUrl = `${window.location.origin}/api/share/venues/${shareSlugOrId}`;
  const whatsappText = `Book at ${facility.name}: ${publicUrl}`;

  const shareVenue = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: facility.name, text: `Book at ${facility.name}`, url: shareApiUrl });
        return;
      }
    } catch (e) {
      // User cancelled or share failed — fall through to clipboard fallback.
    }
    try {
      await navigator.clipboard.writeText(shareApiUrl);
      toast({ title: 'Share link copied' });
    } catch (e) {
      toast({ title: 'Could not copy link', variant: 'destructive' });
    }
  };

  const copyWhatsappText = async () => {
    try {
      await navigator.clipboard.writeText(whatsappText);
      toast({ title: 'WhatsApp message copied' });
    } catch (e) {
      toast({ title: 'Could not copy message', variant: 'destructive' });
    }
  };

  const getOperatingHours = () => {
    if (facility.operating_hours) {
      const s = facility.operating_hours.start || '06:00';
      const e = facility.operating_hours.end || '23:00';
      const fmt = (t) => { const h = parseInt(t.split(':')[0]); return h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h-12} PM`; };
      return `${fmt(s)} - ${fmt(e)}`;
    }
    return '6 AM - 11 PM';
  };

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Hero */}
      <section className="relative h-56 sm:h-72" data-testid="venue-hero">
        <img src={heroImg} alt={facility.name} decoding="async" fetchPriority="high" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center"
          data-testid="venue-back-btn"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>

        {/* Favourite button */}
        {isAuthenticated && (
          <button
            onClick={toggleFavourite}
            className="absolute top-4 w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center z-10"
            style={{ right: '16px' }}
            data-testid="venue-favourite-btn"
          >
            <Heart className={`w-5 h-5 ${isFavourite ? 'text-red-400 fill-red-400' : 'text-white'}`} />
          </button>
        )}

        {/* Tags on hero */}
        <div className="absolute top-4 flex gap-1.5" style={{ right: isAuthenticated ? '60px' : '16px' }}>
          {sports.map(s => (
            <span key={s} className="px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm text-xs font-heading font-semibold text-white">
              {SPORT_LABELS[s] || s}
            </span>
          ))}
        </div>

        {/* Venue name on hero */}
        <div className="absolute bottom-4 left-4 right-4">
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white leading-tight">{facility.name}</h1>
          <div className="flex items-center gap-3 mt-1 text-white/70 text-sm font-body">
            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {facility.location || 'Bengaluru'}</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {getOperatingHours()}</span>
          </div>
        </div>
      </section>

      {/* Action Buttons */}
      <section className="px-4 py-4" data-testid="venue-actions">
        <div className="max-w-md mx-auto grid grid-cols-4 gap-2">
          <button
            onClick={() => navigate(`/book/${facility.id}`)}
            className="py-3 rounded-full bg-[#F97316] text-white font-heading font-semibold text-xs sm:text-sm transition-colors hover:bg-[#ea6a0c]"
            data-testid="venue-book-btn"
          >
            Book
          </button>
          <button
            onClick={() => document.getElementById('venue-games')?.scrollIntoView({ behavior: 'smooth' })}
            className="py-3 rounded-full bg-[#0A2C93] text-white font-heading font-semibold text-xs sm:text-sm transition-colors hover:bg-[#082273]"
            data-testid="venue-join-game-btn"
          >
            Join Game
          </button>
          <button
            onClick={() => document.getElementById('venue-events')?.scrollIntoView({ behavior: 'smooth' })}
            className="py-3 rounded-full bg-[#0A2C93] text-white font-heading font-semibold text-xs sm:text-sm transition-colors hover:bg-[#082273]"
            data-testid="venue-events-btn"
          >
            Events
          </button>
          <button
            onClick={() => document.getElementById('venue-coaching')?.scrollIntoView({ behavior: 'smooth' })}
            className="py-3 rounded-full bg-[#0A2C93] text-white font-heading font-semibold text-xs sm:text-sm transition-colors hover:bg-[#082273]"
            data-testid="venue-coaching-btn"
          >
            Coaching
          </button>
        </div>

        {/* Share row */}
        <div className="max-w-md mx-auto grid grid-cols-2 gap-2 mt-2">
          <button
            onClick={shareVenue}
            className="py-2.5 rounded-full bg-white border border-[#0A2C93]/15 text-[#0A2C93] font-heading font-semibold text-xs flex items-center justify-center gap-1.5 transition-colors hover:bg-[#0A2C93]/5"
            data-testid="venue-share-btn"
          >
            <Share2 className="w-3.5 h-3.5" /> Share venue
          </button>
          <button
            onClick={copyWhatsappText}
            className="py-2.5 rounded-full bg-white border border-[#0A2C93]/15 text-[#0A2C93] font-heading font-semibold text-xs flex items-center justify-center gap-1.5 transition-colors hover:bg-[#0A2C93]/5"
            data-testid="venue-copy-whatsapp-btn"
          >
            <MessageCircle className="w-3.5 h-3.5" /> Copy WhatsApp text
          </button>
        </div>
      </section>

      {/* Join Games at this venue — horizontal scroll */}
      <section id="venue-games" className="px-4 py-6 bg-[#F8FAFC]" data-testid="venue-games-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading font-bold text-lg text-[#0A2C93] mb-3">Join Games</h2>
          {gamesError ? (
            <div className="bg-white rounded-2xl border border-red-100 p-6 text-center" data-testid="venue-games-error">
              <p className="font-body text-sm text-red-600">{gamesError}</p>
            </div>
          ) : games.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center">
              <p className="font-body text-sm text-[#737373]">No upcoming games at this venue</p>
            </div>
          ) : (
            <div className="flex overflow-x-auto gap-3 pb-2 scrollbar-hide snap-x" style={{ scrollbarWidth: 'none' }}>
              {games.slice(0, 5).map(game => (
                <button
                  key={game.id || game._id}
                  onClick={() => navigate(`/community/${facility.slug || venueId}/game/${game.id || game._id}`)}
                  className="flex-shrink-0 snap-start bg-white border border-slate-100 rounded-2xl p-4 text-left hover:-translate-y-0.5 transition-all"
                  style={{ width: '280px', boxShadow: '0 4px 15px rgb(0 0 0 / 0.04)' }}
                  data-testid={`venue-game-${game.id || game._id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-heading font-bold text-sm text-[#0A2C93]">
                      {game.sport || 'Football'} - {game.format || ''}
                    </span>
                    <span className="font-heading font-bold text-sm text-[#00D650]">₹{game.cost_per_player || 0}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs font-body text-[#737373]">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {game.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {game.start_time}</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {(game.spots_left != null) ? game.spots_left : Math.max(0, (game.max_players || 0) - (game.players_filled || 0))} spots</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Upcoming Events — horizontal scroll, only if events exist */}
      {upcomingEvents.length > 0 && (
        <section id="venue-events" className="px-4 py-6" data-testid="venue-upcoming-events">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-lg text-[#0A2C93]">Upcoming Events</h2>
              <button
                onClick={() => navigate(`/events?venue=${encodeURIComponent(facility.id)}`)}
                className="text-xs font-semibold text-[#0A2C93] hover:underline"
                data-testid="venue-view-all-events"
              >
                View all events &rarr;
              </button>
            </div>
            <div className="flex overflow-x-auto gap-3 pb-2 scrollbar-hide snap-x" style={{ scrollbarWidth: 'none' }}>
              {upcomingEvents.map(ev => (
                <div key={ev.id} className="flex-shrink-0 snap-start" style={{ width: '260px' }}>
                  <EventCard event={ev} onClick={() => navigate(`/events/${ev.slug}`)} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Coaching — horizontal scroll, only if programmes exist */}
      {coachingProgrammes.length > 0 && (
        <section id="venue-coaching" className="px-4 py-6 bg-[#F8FAFC]" data-testid="venue-coaching-section">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-lg text-[#0A2C93]">Coaching</h2>
              <button
                onClick={() => navigate(`/coaching?venue=${encodeURIComponent(facility.id)}`)}
                className="text-xs font-semibold text-[#0A2C93] hover:underline"
                data-testid="venue-view-all-coaching"
              >
                View all &rarr;
              </button>
            </div>
            <div className="flex overflow-x-auto gap-3 pb-2 scrollbar-hide snap-x" style={{ scrollbarWidth: 'none' }}>
              {coachingProgrammes.map(p => (
                <div key={p.id} className="flex-shrink-0 snap-start" style={{ width: '260px' }}>
                  <CoachingCard programme={p} onClick={() => navigate(`/coaching/${p.slug}`)} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="px-4 py-6 bg-[#F8FAFC]" data-testid="venue-features">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading font-bold text-lg text-[#0A2C93] mb-4">Features</h2>
          <div className="grid grid-cols-4 gap-3">
            {FEATURES.map(feat => (
              <div key={feat.label} className="flex flex-col items-center gap-2 py-3">
                <div className="w-11 h-11 rounded-xl bg-white border border-slate-100 flex items-center justify-center">
                  <feat.icon className="w-5 h-5 text-[#0A2C93]" />
                </div>
                <span className="font-body text-[11px] text-[#737373] text-center">{feat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Courts */}
      <section className="px-4 py-6" data-testid="venue-courts">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading font-bold text-lg text-[#0A2C93] mb-3">Courts ({courts.length})</h2>
          <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
            {courts.map(court => (
              <div key={court.id} className="flex-shrink-0 px-3 py-2 rounded-xl bg-[#0A2C93]/5 border border-[#0A2C93]/10">
                <span className="font-heading font-semibold text-xs text-[#0A2C93]">{court.name || court.id}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Venues */}
      {relatedVenues.length > 0 && (
        <section className="px-4 py-6 bg-[#F8FAFC]" data-testid="related-venues">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading font-bold text-lg text-[#0A2C93] mb-3">Related Venues</h2>
            <div className="flex overflow-x-auto gap-3 pb-2 scrollbar-hide">
              {relatedVenues.map(rv => {
                const img = FACILITY_IMAGES[rv.id] || DEFAULT_VENUE_IMAGE;
                return (
                  <button
                    key={rv.id}
                    onClick={() => navigate(`/venue/${rv.slug || rv.id}`)}
                    className="flex-shrink-0 w-[180px] bg-white rounded-2xl border border-slate-100 overflow-hidden text-left"
                    data-testid={`related-venue-${rv.id}`}
                  >
                    <img src={img} alt={rv.name} loading="lazy" decoding="async" className="w-full h-24 object-cover" />
                    <div className="p-2">
                      <h3 className="font-heading font-bold text-xs text-[#0A2C93] truncate">{rv.name}</h3>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Sticky Book Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-slate-100 p-3 z-50">
        <div className="max-w-md mx-auto flex gap-2">
          <button
            onClick={() => navigate(`/book/${facility.id}`)}
            className="flex-1 py-3 rounded-full bg-[#00D650] hover:bg-[#00C248] text-[#0A2C93] font-heading font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid="venue-sticky-book-btn"
          >
            <Calendar className="w-4 h-4" /> Book Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default VenueDetail;
